import type { ReactNode } from 'react';
import type { Attribution } from './api.js';
import { formatStamp } from './format.js';

/**
 * The furniture every widget shares: header, footer, loading and error states.
 *
 * Kept in one place so all three widgets are visibly the same product, and so
 * the attribution footer cannot be forgotten in one of them — that is a licence
 * obligation for several providers, not a design choice.
 */

interface FrameProps {
  title: string;
  /** Formatted upstream observation time. */
  stamp?: string;
  stale?: boolean;
  /** Every provider that contributed and asks to be credited. */
  attributions: Attribution[];
  locale: string;
  children: ReactNode;
}

export function Frame({ title, stamp, stale, attributions, children }: FrameProps) {
  return (
    <div className="widget">
      <div className="widget-head">
        <h2 className="widget-title">{title}</h2>
        {stamp && (
          <span className="widget-stamp">
            {stamp}
            {stale && (
              <span className="stale-badge" title="A refresh failed; showing the last known data.">
                stale
              </span>
            )}
          </span>
        )}
      </div>

      {children}

      {attributions.length > 0 && (
        <div className="widget-foot">
          {/* A merged data type credits several institutions at once — the
              calendar is the union of the Fed's diary, BEA releases and ECB
              press output, and each licence wants naming. Separated by a thin
              space and a bullet so the footer stays one quiet line. */}
          {attributions.map((a, i) => (
            <span key={a.text}>
              {i > 0 && <span className="attr-sep"> • </span>}
              {a.url ? (
                <a href={a.url} target="_blank" rel="noopener noreferrer nofollow">
                  {a.text}
                </a>
              ) : (
                a.text
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton rows rather than a spinner.
 *
 * The widget's height is negotiated with the parent page, and a spinner is a
 * different height from the table that replaces it — so a spinner would make
 * the broker's layout jump the moment data arrived. Skeletons at roughly the
 * final height do not.
 */
export function Loading({ title, rows = 6 }: { title: string; rows?: number }) {
  return (
    <div className="widget">
      <div className="widget-head">
        <h2 className="widget-title">{title}</h2>
      </div>
      <div aria-busy="true" aria-live="polite">
        <span className="state" style={{ display: 'none' }}>
          Loading…
        </span>
        {Array.from({ length: rows }, (_, i) => (
          <div className="skeleton-row" key={i} style={{ width: `${92 - (i % 3) * 12}%` }} />
        ))}
      </div>
    </div>
  );
}

export function ErrorState({ title, message }: { title: string; message: string }) {
  return (
    <div className="widget">
      <div className="widget-head">
        <h2 className="widget-title">{title}</h2>
      </div>
      <p className="state" role="status">
        {message}
      </p>
    </div>
  );
}

/**
 * Shown when every provider for this data type is down, or the data we hold has
 * aged past its ceiling.
 *
 * Deliberately calmer than ErrorState. An error means the embedder did
 * something wrong and can fix it; this means we have nothing to show and they
 * can do nothing about it. Rendering a red failure box on a broker's page for
 * our outage would be both unhelpful and bad manners, so this states the
 * position plainly and says the widget is still trying.
 *
 * When a last-known timestamp exists it is shown, because "we had data an hour
 * ago" and "we have never had data" are very different situations for whoever
 * is looking at it.
 */
export function UnavailableState({
  title,
  message,
  lastGoodAt,
  locale,
  timeZone,
}: {
  title: string;
  message: string;
  lastGoodAt?: string;
  locale: string;
  timeZone: string;
}) {
  return (
    <div className="widget">
      <div className="widget-head">
        <h2 className="widget-title">{title}</h2>
      </div>
      <p className="state" role="status">
        {message}
        {lastGoodAt && (
          <>
            <br />
            <span className="muted">
              Last update {formatStamp(lastGoodAt, locale, timeZone)}
            </span>
          </>
        )}
      </p>
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <p className="state" role="status">
      {message}
    </p>
  );
}
